<template>
  <div class="my-learning-container">
    <div class="section-inner">
      <!-- Tab Switch -->
      <div class="tab-bar">
        <div
          class="tab-item"
          :class="{ active: activeTab === 'in_progress' }"
          @click="switchTab('in_progress')"
        >In Progress</div>
        <div
          class="tab-item tab-completed"
          :class="{ active: activeTab === 'completed' }"
          @click="switchTab('completed')"
        >Completed</div>
      </div>

      <!-- Loading -->
      <div v-loading="loading" style="min-height: 200px;">
        <!-- In Progress List -->
        <template v-if="activeTab === 'in_progress'">
          <div v-if="inProgressList.length > 0" class="course-list">
            <div
              v-for="course in inProgressList"
              :key="course.collection_id"
              class="course-card"
            >
              <div class="course-cover">
                <CachedImage
                  v-if="course.cover_url"
                  :src="course.cover_url"
                  :fallback-src="placeholderImage"
                  fit="cover"
                  width="100%"
                  height="100%"
                  lazy
                />
                <div v-else class="cover-placeholder">
                  <i class="el-icon-reading"></i>
                </div>
              </div>

              <div class="course-info">
                <div class="course-author">
                  <el-avatar :size="20" :src="course.user_avatar_address_small" class="author-avatar">
                    {{ course.user_name ? course.user_name.charAt(0) : '?' }}
                  </el-avatar>
                  <span>{{ course.user_name || 'Unknown' }}</span>
                </div>
                <h3 class="course-title">{{ course.title }}</h3>
                <p class="course-desc">{{ course.description || 'No description' }}</p>
              </div>

              <div class="course-actions">
                <div class="progress-info">
                  <span class="progress-text">Progress: {{ getProgressPercent(course) }}%</span>
                  <el-progress
                    :percentage="getProgressPercent(course)"
                    :stroke-width="8"
                    :show-text="false"
                    :color="getProgressColor(course.quiz_answer_count, course.quiz_count)"
                  ></el-progress>
                </div>
                <div class="action-buttons">
                  <el-button
                    size="small"
                    type="primary"
                    @click="handleContinue(course)"
                  >Continue</el-button>
                  <el-dropdown trigger="click" @command="cmd => handleMoreCommand(cmd, course)">
                    <el-button size="small" class="btn-more">
                      <i class="el-icon-more"></i>
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="remove" class="remove-item">
                        <i class="el-icon-delete" style="color: #F56C6C;"></i> Remove
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="!loading" class="empty-state">
            <i class="el-icon-reading"></i>
            <h3>No courses in progress</h3>
            <p>Start learning a course from the Plaza to see it here</p>
          </div>
        </template>

        <!-- Completed List -->
        <template v-if="activeTab === 'completed'">
          <div v-if="completedList.length > 0" class="course-list">
            <div
              v-for="course in completedList"
              :key="course.collection_id"
              class="course-card"
            >
              <div class="course-cover">
                <CachedImage
                  v-if="course.cover_url"
                  :src="course.cover_url"
                  :fallback-src="placeholderImage"
                  fit="cover"
                  width="100%"
                  height="100%"
                  lazy
                />
                <div v-else class="cover-placeholder">
                  <i class="el-icon-reading"></i>
                </div>
              </div>

              <div class="course-info">
                <div class="course-author">
                  <el-avatar :size="20" :src="course.user_avatar_address_small" class="author-avatar">
                    {{ course.user_name ? course.user_name.charAt(0) : '?' }}
                  </el-avatar>
                  <span>{{ course.user_name || 'Unknown' }}</span>
                </div>
                <h3 class="course-title">{{ course.title }}</h3>
                <p class="course-desc">{{ course.description || 'No description' }}</p>
              </div>

              <div class="course-actions">
                <div class="progress-info">
                  <span class="progress-text completed-text">Completed</span>
                  <el-progress
                    :percentage="100"
                    :stroke-width="8"
                    :show-text="false"
                    color="#67C23A"
                  ></el-progress>
                </div>
                <div class="action-buttons">
                  <el-button
                    v-if="course.has_exam"
                    size="small"
                    type="primary"
                    @click="handleAssessment(course)"
                  >Assessment</el-button>
                  <el-button
                    size="small"
                    type="primary"
                    @click="handleRead(course)"
                  >Read</el-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="!loading" class="empty-state">
            <i class="el-icon-circle-check" style="color: #67C23A;"></i>
            <h3>No completed courses</h3>
            <p>Complete all quizzes in a course to see it here</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CachedImage from '@/components/CachedImage.vue'
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'

export default {
  name: 'MyLearning',
  components: { CachedImage },
  data() {
    return {
      activeTab: 'in_progress',
      placeholderImage: require('@/assets/Card Collection Cover Placeholder.jpeg'),
      loading: false,
      inProgressList: [],
      completedList: []
    }
  },
  computed: {
    ...mapState(['user_id'])
  },
  mounted() {
    this.fetchLearningCollections()
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab
    },

    async fetchLearningCollections() {
      this.loading = true
      try {
        const response = await postRequest('getUserLearningCollections', {
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.inProgressList = response.data.data.in_progress || []
          this.completedList = response.data.data.completed || []
        }
      } catch (error) {
        console.error('Error fetching learning collections:', error)
        this.$message.error('Failed to load learning data')
      } finally {
        this.loading = false
      }
    },

    getProgressPercent(course) {
      if (!course.quiz_count || course.quiz_count === 0) return 0
      return Math.min(100, Math.round((course.quiz_answer_count / course.quiz_count) * 100))
    },

    getProgressColor(answered, total) {
      const pct = (answered / total) * 100
      if (pct >= 100) return '#67C23A'
      if (pct >= 50) return '#409EFF'
      if (pct >= 25) return '#E6A23C'
      return '#F56C6C'
    },

    handleMoreCommand(command, course) {
      if (command === 'remove') {
        this.handleRemove(course)
      }
    },

    handleRemove(course) {
      this.$confirm(
        `Are you sure you want to remove "${course.title}" from your learning list? This will clear all your quiz answers for this course.`,
        'Remove Course',
        {
          confirmButtonText: 'Remove',
          cancelButtonText: 'Cancel',
          type: 'info',
          confirmButtonClass: 'el-button--primary'
        }
      ).then(async () => {
        try {
          const response = await postRequest('removeUserQuizAnswersForCollection', {
            user_id: this.user_id,
            collection_id: course.collection_id
          })
          if (response.data.code === 0) {
            this.inProgressList = this.inProgressList.filter(
              c => c.collection_id !== course.collection_id
            )
            this.completedList = this.completedList.filter(
              c => c.collection_id !== course.collection_id
            )
            this.$message.success('Course removed successfully')
          } else {
            this.$message.error(response.data.msg || 'Failed to remove course')
          }
        } catch (error) {
          console.error('Error removing course:', error)
          this.$message.error('Failed to remove course')
        }
      }).catch(() => {})
    },

    handleContinue(course) {
      this.$store.dispatch('setTempCollectionId', course.collection_id)
      const route = this.$router.resolve({ path: '/read-collection', query: { source: 'learning', id: course.collection_id } })
      window.open(route.href, '_blank')
    },

    handleRead(course) {
      this.$store.dispatch('setTempCollectionId', course.collection_id)
      const route = this.$router.resolve({ path: '/read-collection', query: { source: 'learning', id: course.collection_id } })
      window.open(route.href, '_blank')
    },

    async handleAssessment(course) {
      try {
        const response = await postRequest('canStartExam', {
          collection_id: course.collection_id,
          user_id: this.user_id
        })
        if (response.data.code === 0 && response.data.data.can_start) {
          // Can start exam, navigate to prep page
          this.$router.push({
            path: '/exam-prep',
            query: { collection_id: course.collection_id }
          })
        } else if (response.data.data?.reason === 'max_attempts') {
          this.$message.warning("You've reached the maximum number of attempts.")
        } else if (response.data.data?.reason === 'cooldown') {
          const lastInfo = await postRequest('getExamInfo', {
            collection_id: course.collection_id,
            user_id: this.user_id
          })
          const lastTime = lastInfo.data.data?.last_attempt_time
          const cooldownEnd = response.data.data?.cooldown_end
          const prevStr = lastTime ? new Date(lastTime * 1000).toLocaleString() : 'N/A'
          const nextStr = cooldownEnd ? new Date(cooldownEnd * 1000).toLocaleString() : 'N/A'
          this.$message.warning(`You last attempted the exam at ${prevStr}. Please wait until ${nextStr} to try again.`)
        } else if (response.data.data?.reason === 'exam_not_enabled') {
          this.$message.warning('The exam is not enabled for this course.')
        } else {
          console.log('canStartExam response:', response.data)
          this.$message.warning(response.data.msg || 'Cannot start exam at this time')
        }
      } catch (error) {
        console.error('Error checking exam status:', error)
        this.$message.error('Failed to check exam status')
      }
    }
  }
}
</script>

<style scoped>
.my-learning-container {
  width: 100%;
  min-height: 100vh;
  background: #f7f8fc;
}

.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Tab Bar */
.tab-bar {
  display: inline-flex;
  gap: 0;
  padding: 24px 0 24px;
  border-radius: 6px;
  overflow: hidden;
}

.tab-item {
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
}

.tab-item:first-child {
  border-radius: 6px 0 0 6px;
}

.tab-item:last-child {
  border-radius: 0 6px 6px 0;
  border-left: none;
}

.tab-item.active {
  background: #409eff;
  color: #fff;
  border-color: #409eff;
}

.tab-completed.active {
  background: #67C23A !important;
  border-color: #67C23A !important;
}

.tab-item:not(.active):hover {
  color: #409eff;
}

/* Course List */
.course-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 60px;
}

.course-card {
  display: flex;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid #ebeef5;
  transition: box-shadow 0.2s;
}

.course-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* Left: Cover */
.course-cover {
  width: 160px;
  min-width: 160px;
  aspect-ratio: 5 / 7;
  background: #eef1f6;
  flex-shrink: 0;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.cover-placeholder i {
  font-size: 36px;
  color: #b0bac5;
}

/* Center: Info */
.course-info {
  flex: 1;
  padding: 20px 24px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.course-author {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.author-avatar {
  background: #e0e0e0;
  color: #606266;
  font-size: 10px;
  flex-shrink: 0;
}

.course-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.course-desc {
  margin: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Right: Actions */
.course-actions {
  width: 200px;
  min-width: 200px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  border-left: 1px solid #f2f3f5;
}

.progress-info {
  width: 100%;
  text-align: right;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  display: block;
}

.completed-text {
  color: #67C23A;
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
}

.action-buttons .el-button {
  margin-left: 0;
}

.action-buttons .el-button:first-child {
  flex: 1;
}

.btn-more {
  padding: 7px 8px;
  width: auto !important;
}

.btn-more .el-icon-more {
  transform: rotate(90deg);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 56px;
  color: #dcdfe6;
}

.empty-state h3 {
  margin: 16px 0 8px;
  color: #606266;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .course-card {
    flex-direction: column;
  }

  .course-cover {
    width: 100%;
    min-width: 0;
    aspect-ratio: 5 / 7;
  }

  .course-actions {
    width: 100%;
    min-width: 0;
    border-left: none;
    border-top: 1px solid #f2f3f5;
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }

  .action-buttons {
    flex-direction: row;
    width: auto;
  }

  .action-buttons .el-button {
    width: auto;
  }

  .section-inner {
    padding: 0 16px;
  }
}
</style>

<style>
.confirm-remove-btn span::before {
  content: "\e6d7";
  font-family: "element-icons" !important;
  margin-right: 4px;
  font-style: normal;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
}

.remove-item {
  color: #F56C6C !important;
}
</style>
